import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Container, Typography, useMediaQuery, useTheme } from '@mui/material';

const PageHeader = ({ title, subtitle, py = 8, height, backgroundImage, showBackButton = false, metadata = [] }) => {
  const theme = useTheme();
  const isTabletDown = useMediaQuery(theme.breakpoints.down('md'));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: isTabletDown ? 0 : -30, y: isTabletDown ? 20 : 0 },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1],
      },
    },
  };

  const watermarkVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 0.05,
      x: 0,
      transition: {
        duration: 2,
        ease: 'easeOut',
      },
    },
  };

  const subtitleVariants = {
    hidden: { opacity: 0, scale: 0.98, y: 10 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 1,
        ease: [0.16, 1, 0.3, 1], // easeOutExpo
      },
    },
  };

  return (
    <Box
      sx={{
        bgcolor: 'primary.main',
        position: 'relative',
        height: height || 'auto',
        // Adaptive height scale
        minHeight: height ? 'auto' : {
          xs: '320px',
          sm: '400px',
          md: '50vh',
          lg: '60vh',
        },
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        color: 'white',
        py: height ? 0 : py,
        zIndex: 1,
        // Background Image Layer with Zoom
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: { xs: 'center 40%', md: 'center' },
          backgroundRepeat: 'no-repeat',
          zIndex: -2,
          transform: 'scale(1.05)',
          animation: 'headerZoom 30s infinite alternate ease-in-out',
        },
        // Cinematic Spotlight Overlay
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: {
            xs: 'linear-gradient(180deg, rgba(6, 19, 25, 0.75) 0%, rgba(6, 19, 25, 0.95) 100%)',
            md: 'radial-gradient(circle at 75% 50%, transparent 0%, rgba(6, 19, 25, 0.85) 60%, rgba(6, 19, 25, 0.95) 100%)',
          },
          zIndex: -1,
        },
        '@keyframes headerZoom': {
          '0%': { transform: 'scale(1) rotate(0.01deg)' },
          '100%': { transform: 'scale(1.1) rotate(0.01deg)' },
        },
      }}
    >
      {/* Background Watermark Title - Scaling for large screens, hidden on Tablet */}
      {!isTabletDown && (
        <Typography
          component={motion.div}
          variants={watermarkVariants}
          initial="hidden"
          animate="visible"
          sx={{
            position: 'absolute',
            left: '5%',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: { md: '12rem', lg: '15rem', xl: '18rem' },
            fontWeight: 900,
            color: 'white',
            whiteSpace: 'nowrap',
            lineHeight: 1,
            zIndex: 0,
            textTransform: 'uppercase',
            fontFamily: 'Bebas Neue',
            pointerEvents: 'none',
            userSelect: 'none',
            opacity: 0.05,
          }}
        >
          {title}
        </Typography>
      )}

      {/* Fluid Padding Container */}
      <Container
        maxWidth="xl"
        sx={{
          zIndex: 1,
          position: 'relative',
          alignSelf: "flex-end",
          px: { xs: 3, sm: 5, md: 8, lg: 12 }
        }}
      >
        <Box
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-start' },
            textAlign: { xs: 'center', md: 'left' },
            py: 6
          }}
        >
          {/* Back Button */}
          {showBackButton && (
            <Box
              component={motion.div}
              variants={itemVariants}
              sx={{
                mb: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                opacity: 0.7,
                transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                color: 'white',
                '&:hover': {
                  opacity: 1,
                  transform: 'translateX(-8px)',
                  '& .back-icon-circle': {
                      bgcolor: 'rgba(255,255,255,0.2)',
                      borderColor: 'rgba(255,255,255,0.4)',
                  }
                }
              }}
              onClick={() => window.history.back()}
            >
              <Box
                className="back-icon-circle"
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid rgba(255,255,255,0.2)',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  transition: 'all 0.4s ease'
                }}
              >
                  <ArrowBackIcon sx={{ fontSize: '1.2rem' }} />
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', letterSpacing: 2, textTransform: 'uppercase' }}>
                Back to visual chronicles
              </Typography>
            </Box>
          )}

          {/* Title Box */}
          <Box sx={{ position: 'relative', mb: { xs: subtitle ? 2 : 0, md: subtitle ? 4 : 0 } }}>
            <motion.div variants={itemVariants}>
              <Typography
                variant="h1"
                sx={{
                  fontFamily: 'Bebas Neue',
                  fontSize: {
                    xs: '2rem',
                    sm: '3.5rem',
                    md: '5rem',
                    lg: '7rem',
                  },
                  fontWeight: 900,
                  letterSpacing: '0.01em',
                  lineHeight: { xs: 1, md: 0.85 },
                  textShadow: '4px 4px 20px rgba(0,0,0,0.6)',
                  background: 'linear-gradient(180deg, #FFFFFF 40%, #CCCCCC 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {title}
              </Typography>
            </motion.div>
          </Box>

          {/* Subtitle Glass Container */}
          {subtitle && (
            <motion.div variants={subtitleVariants} style={{ width: '100%' }}>
              <Box
                sx={{
                  borderRadius: 4,
                  p: { xs: 0, sm: 2, md: 3 },
                  maxWidth: { xs: '100%', sm: '85%', md: '900px' },
                  backdropFilter: { xs: 'none', md: 'blur(12px)' },
                  willChange: 'transform, opacity, backdrop-filter',
                  boxShadow: { xs: 'none', md: '0 20px 50px rgba(0,0,0,0.4)' },
                  bgcolor: { xs: 'transparent', md: 'rgba(255, 255, 255, 0.04)' },
                  border: { xs: 'none', md: '1px solid rgba(255, 255, 255, 0.1)' },
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 400,
                    lineHeight: { xs: 1.4, md: 1.7 },
                    color: 'rgba(255,255,255,0.9)',
                    letterSpacing: '0.01em',
                    fontSize: {
                      xs: '.9rem',
                      sm: '1.1rem',
                      md: '1.3rem',
                      lg: '1.5rem',
                    },
                    textShadow: '1px 1px 4px rgba(0,0,0,0.2)',
                  }}
                >
                  {subtitle}
                </Typography>
              </Box>
            </motion.div>
          )}

          {/* Metadata Chips */}
          {metadata && metadata.length > 0 && (
            <Box 
                component={motion.div}
                variants={itemVariants}
                sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 2, 
                    mt: 4,
                    justifyContent: { xs: 'center', md: 'flex-start' }
                }}
            >
                {metadata.map((item, idx) => (
                    <Box 
                      key={idx}
                      sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5, 
                          bgcolor: 'rgba(255,255,255,0.06)', 
                          px: 2.5, 
                          py: 1, 
                          borderRadius: '12px',
                          border: '1px solid rgba(255,255,255,0.1)',
                          backdropFilter: 'blur(4px)'
                      }}
                    >
                        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 800, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 2 }}>
                            {item.label}
                        </Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '0.95rem', fontFamily: 'Bebas Neue', letterSpacing: 1 }}>
                            {item.value}
                        </Typography>
                    </Box>
                ))}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default PageHeader;
